import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import {
  ShieldCheck, LogOut, Users, Clock, ClipboardCheck, Flag, Table2, ScrollText,
  QrCode, Settings as SettingsIcon, Plus, Pencil, Trash2, Download, Check, X,
  ChevronLeft, ChevronRight, AlertTriangle, Home, MapPin, Mail, RefreshCw, CalendarRange,
  Sunrise, LogIn, History, Search, Lock, Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { fmtDateTime, fmtTime, fmtISODate, toLocalInput } from "@/lib/time";
import { Footer } from "@/components/Footer";

const LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/wingman/da9c2719-2c74-4d76-b1c3-a7e2071c9ed9/attachments/72ffa52fcb5a4ae8bceb3e4a0c7a1cce_rosemount-joinery-logo-scaled.jpg";
const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const CATEGORIES = [
  { value: "annual_loading", label: "Annual Leave Loading" },
  { value: "annual_pay", label: "Annual Leave Pay" },
  { value: "base", label: "Base Hourly" },
  { value: "ot15", label: "Overtime (1.5x)" },
  { value: "ot2", label: "Overtime (2x)" },
  { value: "personal_pay", label: "Personal Leave Pay" },
  { value: "public_holiday", label: "Public Holiday" },
];
const CAT_SHORT = {
  annual_loading: "AL Loading",
  annual_pay: "AL Pay",
  base: "Base",
  ot15: "OT 1.5x",
  ot2: "OT 2x",
  personal_pay: "PL Pay",
  public_holiday: "Pub Hol",
};

const round2 = (n) => Math.round(n * 100) / 100;
// Mirror of backend split_hours: Mon–Thu base ≤8, Fri base ≤6, Sat all OT 1.5x, Sun all OT 2x.
function splitHours(payable, weekday) {
  let std = 0, ot15 = 0, ot2 = 0;
  if (weekday <= 3) { std = Math.min(payable, 8); ot15 = Math.min(Math.max(payable - 8, 0), 2); ot2 = Math.max(payable - 10, 0); }
  else if (weekday === 4) { std = Math.min(payable, 6); ot15 = Math.min(Math.max(payable - 6, 0), 2); ot2 = Math.max(payable - 8, 0); }
  else if (weekday === 5) { ot15 = payable; }
  else { ot2 = payable; }
  return { base: round2(std), ot15: round2(ot15), ot2: round2(ot2) };
}

export default function Admin() {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(!!localStorage.getItem("rms_token"));

  useEffect(() => {
    if (!authed) return;
    api.get("/auth/me").catch(() => {
      localStorage.removeItem("rms_token");
      setAuthed(false);
    });
  }, [authed]);

  if (!authed) return <Login onOk={() => setAuthed(true)} nav={nav} />;
  return <Dashboard onLogout={() => { localStorage.removeItem("rms_token"); setAuthed(false); }} nav={nav} />;
}

function Login({ onOk, nav }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("rms_token", data.token);
      onOk();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 relative">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src={LOGO} alt="Rosemount Joinery" className="h-14 mx-auto mb-4 bg-white rounded-sm p-2" />
          <h1 className="text-white font-heading font-bold text-2xl flex items-center justify-center gap-2">
            <ShieldCheck className="w-6 h-6 text-red-500" /> Admin Login
          </h1>
        </div>
        <form onSubmit={submit} className="bg-white rounded-sm p-6 space-y-4">
          <div>
            <Label className="text-sm mb-1 block">Username</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-sm border-slate-300 focus-visible:ring-red-600" data-testid="admin-email-input" />
          </div>
          <div>
            <Label className="text-sm mb-1 block">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-sm border-slate-300 focus-visible:ring-red-600" data-testid="admin-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-sm bg-red-600 hover:bg-red-700" data-testid="admin-login-submit">
            {busy ? "Signing in…" : "Sign In"}
          </Button>
          <button type="button" onClick={() => nav("/")} className="w-full text-sm text-slate-500 hover:text-slate-800 flex items-center justify-center gap-1">
            <Home className="w-4 h-4" /> Back to clock-in
          </button>
        </form>
      </div>
      <Footer className="absolute bottom-0 left-0 right-0 text-slate-400/70" />
    </div>
  );
}

const Stat = ({ label, value, tone, testid }) => (
  <Card className="rounded-sm border-border p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
    <div className={`font-heading font-bold text-3xl mt-1 ${tone || "text-foreground"}`}>{value}</div>
  </Card>
);

/* ---------------- Overview (6 interactive cards) ---------------- */
function LateArrivalRow({ l, onDismiss }) {
  const [note, setNote] = useState(l.late_note || "");
  const [busy, setBusy] = useState(false);
  const dirty = note !== (l.late_note || "");
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/late-arrivals/${l.shift_id}/note`, { note }); toast.success("Note saved"); l.late_note = note; }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="p-4" data-testid={`late-row-${l.shift_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium">{l.name}</div>
          <div className="text-sm text-muted-foreground font-mono">In {fmtDateTime(l.clock_in)} · Scheduled {l.scheduled_start || "—"}</div>
        </div>
        {onDismiss && (
          <Button size="sm" variant="ghost" className="rounded-sm h-8 text-slate-500 hover:text-red-600 shrink-0" onClick={() => onDismiss(l.shift_id)} data-testid={`clear-late-${l.shift_id}`}>
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a reason / note for payroll…" className="rounded-sm border-slate-300 h-8 text-sm" data-testid={`late-note-input-${l.shift_id}`} />
        <Button size="sm" variant="outline" className="rounded-sm h-8 shrink-0" disabled={busy || !dirty} onClick={save} data-testid={`late-note-save-${l.shift_id}`}>Save</Button>
      </div>
    </div>
  );
}

function Overview({ clockedIn, early, late, blocked, alerts, forgot, reload }) {
  const [selected, setSelected] = useState("clockedin");
  const pendingAlerts = alerts.filter((a) => !a.reviewed);
  const forgotIn = forgot.filter((r) => r.type === "in");
  const forgotOut = forgot.filter((r) => r.type === "out");

  const decideEarly = async (id, decision) => {
    try { await api.post(`/admin/early-starts/${id}/${decision}`); toast.success(`Early start ${decision}`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const decideForgot = async (id, decision) => {
    try { await api.post(`/admin/forgot-requests/${id}/${decision}`); toast.success(`Request ${decision}d`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reviewAlert = async (id) => {
    try { await api.post(`/admin/alerts/${id}/reviewed`); toast.success("Marked reviewed"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const cards = [
    { key: "clockedin", label: "Currently Clocked In", icon: Clock, count: clockedIn.length, urgent: false },
    { key: "early", label: "Early Start Approvals", icon: Sunrise, count: early.length, urgent: early.length > 0 },
    { key: "late", label: "Late Arrivals", icon: Flag, count: late.length, urgent: false },
    { key: "blocked", label: "Blocked Clock-Ins", icon: MapPin, count: blocked.length, urgent: blocked.length > 0 },
    { key: "missedout", label: "Missed Clock-Outs", icon: AlertTriangle, count: pendingAlerts.length + forgotOut.length, urgent: (pendingAlerts.length + forgotOut.length) > 0 },
    { key: "missedin", label: "Missed Clock-Ins", icon: LogIn, count: forgotIn.length, urgent: forgotIn.length > 0 },
  ];

  const Empty = ({ children }) => <div className="p-6 text-center text-muted-foreground text-sm">{children}</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const active = selected === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setSelected(c.key)}
              data-testid={`overview-card-${c.key}`}
              className={`text-left rounded-sm border p-4 transition-all hover:shadow-md focus:outline-none ${
                active ? "border-red-600 ring-2 ring-red-600/30 bg-red-50/40" : "border-border bg-card hover:border-red-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`w-4 h-4 ${c.urgent ? "text-red-600" : "text-slate-500"}`} />
                {c.count > 0 && c.urgent && <span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" />}
              </div>
              <div className={`font-heading font-bold text-3xl mt-2 ${c.count > 0 && c.urgent ? "text-red-600" : "text-foreground"}`} data-testid={`overview-count-${c.key}`}>{c.count}</div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mt-1 leading-tight">{c.label}</div>
            </button>
          );
        })}
      </div>

      <Card className="rounded-sm border-border" data-testid={`overview-panel-${selected}`}>
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="font-heading font-semibold">{cards.find((c) => c.key === selected)?.label}</div>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={reload} data-testid="overview-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>

        {selected === "clockedin" && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead><TableHead>Type</TableHead>
                <TableHead>Clocked In</TableHead><TableHead>Duration</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {clockedIn.length === 0 && <TableRow><TableCell colSpan={5}><Empty>Nobody is clocked in right now.</Empty></TableCell></TableRow>}
                {clockedIn.map((c) => (
                  <TableRow key={c.shift_id} data-testid={`clockedin-row-${c.employee_id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.employment_type === "P" ? "Permanent" : "Casual"}</TableCell>
                    <TableCell className="font-mono">{fmtTime(c.clock_in)}</TableCell>
                    <TableCell className="font-mono">{c.duration_hours}h</TableCell>
                    <TableCell>
                      {c.late_arrival && <Badge className="bg-amber-500 rounded-sm mr-1">Late</Badge>}
                      {c.early_start === "pending" && <Badge variant="outline" className="rounded-sm border-slate-400">Early (pending)</Badge>}
                      {!c.late_arrival && c.early_start !== "pending" && <span className="text-muted-foreground text-sm">On time</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {selected === "early" && (
          <div className="divide-y divide-border">
            {early.length === 0 && <Empty>No pending early starts.</Empty>}
            {early.map((e) => (
              <div key={e.shift_id} className="p-4 flex items-center justify-between" data-testid={`early-row-${e.shift_id}`}>
                <div>
                  <div className="font-medium">{e.name}</div>
                  <div className="text-sm text-muted-foreground font-mono">In {fmtTime(e.clock_in)} · Scheduled {e.scheduled_start || "—"}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" onClick={() => decideEarly(e.shift_id, "approved")} data-testid={`early-approve-${e.shift_id}`}><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => decideEarly(e.shift_id, "rejected")} data-testid={`early-reject-${e.shift_id}`}><X className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selected === "late" && (
          <div className="divide-y divide-border max-h-[28rem] overflow-auto">
            {late.length === 0 && <Empty>No late arrivals.</Empty>}
            {late.map((l) => (
              <LateArrivalRow key={l.shift_id} l={l} />
            ))}
          </div>
        )}

        {selected === "blocked" && (
          <div className="divide-y divide-border max-h-[28rem] overflow-auto">
            {blocked.length === 0 && <Empty>No blocked attempts — everyone has clocked in within their geofence.</Empty>}
            {blocked.map((b) => (
              <div key={b.id} className="p-4 flex items-center justify-between gap-3" data-testid={`blocked-row-${b.id}`}>
                <div>
                  <div className="font-medium">{b.employee_name} <Badge variant="outline" className="rounded-sm ml-1">{b.location_name}</Badge></div>
                  <div className="text-sm text-muted-foreground font-mono">
                    {fmtDateTime(b.timestamp)} · {b.reason === "no_gps" ? "No GPS / location denied" : `~${b.distance_m}m away (limit ${b.radius_m}m)`}
                  </div>
                </div>
                <Badge className="bg-red-600 rounded-sm shrink-0">Blocked</Badge>
              </div>
            ))}
          </div>
        )}

        {selected === "missedout" && (
          <div className="divide-y divide-border max-h-[28rem] overflow-auto">
            {pendingAlerts.length === 0 && forgotOut.length === 0 && <Empty>No missed clock-outs to review.</Empty>}
            {pendingAlerts.map((a) => (
              <div key={a.shift_id} className="p-4 flex items-center justify-between" data-testid={`alert-row-${a.shift_id}`}>
                <div>
                  <div className="font-medium">{a.name} <Badge variant="outline" className="rounded-sm ml-1 border-red-300 text-red-600">Auto clock-out</Badge></div>
                  <div className="text-sm text-muted-foreground font-mono">Auto out {fmtDateTime(a.clock_out)}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => reviewAlert(a.shift_id)} data-testid={`review-alert-${a.shift_id}`}>Mark reviewed</Button>
              </div>
            ))}
            {forgotOut.map((r) => (
              <div key={r.id} className="p-4 flex items-start justify-between gap-3" data-testid={`forgot-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{r.employee_name} <Badge variant="outline" className="rounded-sm ml-1">Forgot clock-out</Badge></div>
                  <div className="text-sm text-muted-foreground font-mono">{fmtDateTime(r.submitted_time)}</div>
                  {r.reason && <div className="text-sm text-foreground mt-1">Reason: <span className="text-muted-foreground">{r.reason}</span></div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" onClick={() => decideForgot(r.id, "approve")} data-testid={`forgot-approve-${r.id}`}><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => decideForgot(r.id, "reject")} data-testid={`forgot-reject-${r.id}`}><X className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selected === "missedin" && (
          <div className="divide-y divide-border max-h-[28rem] overflow-auto">
            {forgotIn.length === 0 && <Empty>No missed clock-in requests.</Empty>}
            {forgotIn.map((r) => (
              <div key={r.id} className="p-4 flex items-start justify-between gap-3" data-testid={`forgot-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{r.employee_name} <Badge variant="outline" className="rounded-sm ml-1">Forgot clock-in</Badge></div>
                  <div className="text-sm text-muted-foreground font-mono">{fmtDateTime(r.submitted_time)}</div>
                  {r.reason && <div className="text-sm text-foreground mt-1">Reason: <span className="text-muted-foreground">{r.reason}</span></div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" onClick={() => decideForgot(r.id, "approve")} data-testid={`forgot-approve-${r.id}`}><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => decideForgot(r.id, "reject")} data-testid={`forgot-reject-${r.id}`}><X className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Dashboard({ onLogout, nav }) {
  const [tab, setTab] = useState("overview");
  const [clockedIn, setClockedIn] = useState([]);
  const [early, setEarly] = useState([]);
  const [late, setLate] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [forgot, setForgot] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [pinLockouts, setPinLockouts] = useState([]);
  const [needsPin, setNeedsPin] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setNeedsPin(!r.data.security_pin_configured))
      .catch(() => {});
  }, []);

  const loadOverview = async () => {
    const [ci, e, l, a, f, b, p] = await Promise.allSettled([
      api.get("/admin/clocked-in"),
      api.get("/admin/early-starts"),
      api.get("/admin/late-arrivals"),
      api.get("/admin/alerts"),
      api.get("/admin/forgot-requests"),
      api.get("/admin/blocked-attempts"),
      api.get("/admin/pin-lockouts"),
    ]);
    // Each card updates independently; failed requests keep their last known value.
    if (ci.status === "fulfilled") setClockedIn(ci.value.data);
    if (e.status === "fulfilled") setEarly(e.value.data);
    if (l.status === "fulfilled") setLate(l.value.data);
    if (a.status === "fulfilled") setAlerts(a.value.data);
    if (f.status === "fulfilled") setForgot(f.value.data);
    if (b.status === "fulfilled") setBlocked(b.value.data);
    if (p.status === "fulfilled") setPinLockouts(p.value.data);
  };

  useEffect(() => {
    loadOverview();
    const iv = setInterval(loadOverview, 20000);
    return () => clearInterval(iv);
  }, [tab]);

  const pendingCount = early.length + forgot.length + alerts.filter((a) => !a.reviewed).length + pinLockouts.filter((p) => p.active).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Rosemount" className="h-9 bg-white rounded-sm p-1" />
            <span className="text-white font-heading font-bold hidden sm:block">Staffclock Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm border-slate-600 bg-transparent text-white hover:bg-slate-800 hover:text-white" onClick={() => nav("/")} data-testid="goto-clockin-btn">
              <Home className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Clock-in</span>
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm border-slate-600 bg-transparent text-white hover:bg-slate-800 hover:text-white" onClick={onLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-muted rounded-sm mb-6">
            {[
              ["overview", "Overview", Clock],
              ["employees", "Employees", Users],
              ["approvals", "Approvals", ClipboardCheck],
              ["flags", "Flags & Alerts", Flag],
              ["timesheets", "Timesheets", Table2],
              ["audit", "Audit Log", ScrollText],
              ["qr", "QR Manager", QrCode],
              ["settings", "Settings", SettingsIcon],
            ].map(([v, label, Icon]) => (
              <TabsTrigger key={v} value={v} className="rounded-sm data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid={`tab-${v}`}>
                <Icon className="w-4 h-4 mr-1.5" /> {label}
                {v === "overview" && pendingCount > 0 && (
                  <span
                    className={`ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[10px] font-bold ${tab === "overview" ? "bg-white text-red-600" : "bg-red-600 text-white"}`}
                    data-testid="overview-pending-badge"
                  >
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <Overview clockedIn={clockedIn} early={early} late={late} blocked={blocked} alerts={alerts} forgot={forgot} reload={loadOverview} />
          </TabsContent>

          <TabsContent value="employees"><Employees /></TabsContent>
          <TabsContent value="approvals"><Approvals early={early} forgot={forgot} reload={loadOverview} /></TabsContent>
          <TabsContent value="flags"><Flags late={late} alerts={alerts} blocked={blocked} pinLockouts={pinLockouts} reload={loadOverview} /></TabsContent>
          <TabsContent value="timesheets"><Timesheets /></TabsContent>
          <TabsContent value="audit"><AuditLog /></TabsContent>
          <TabsContent value="qr"><QrManager /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </main>

      {needsPin && <PinSetupDialog onDone={() => setNeedsPin(false)} />}
      <Footer />
    </div>
  );
}

function PinSetupDialog({ onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pin.length !== 4) return;
    setBusy(true);
    try {
      await api.post("/admin/setup-security-pin", { pin });
      toast.success("Security PIN set", { description: "You'll use this to confirm sensitive actions." });
      onDone();
    } catch (e) { toast.error(apiErr(e)); setPin(""); } finally { setBusy(false); }
  };
  return (
    <Dialog open>
      <DialogContent className="rounded-sm max-w-sm max-h-[90vh] overflow-y-auto [&>button]:hidden" data-testid="pin-setup-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-red-600" /> Set your Security PIN
          </DialogTitle>
          <DialogDescription>
            Welcome! Before you continue, set a 4-digit security PIN. It confirms sensitive actions like
            clearing the audit log. You can change it anytime from Settings → Danger Zone.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <Input
            autoFocus
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="••••"
            className="h-14 text-center text-3xl tracking-[0.6em] font-mono rounded-sm border-slate-300 focus-visible:ring-red-600"
            data-testid="pin-setup-input"
          />
          <Button disabled={pin.length !== 4 || busy} onClick={save} className="w-full h-12 rounded-sm bg-red-600 hover:bg-red-700" data-testid="pin-setup-save">
            {busy ? "Saving…" : "Save PIN"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Employees ---------------- */
function EmployeeHistoryDialog({ emp, onClose }) {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/employee-history", { params: { employee_id: emp.id, weeks } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [emp.id, weeks]);

  const grand = data ? round2(data.weeks.reduce((s, w) => s + w.total_hours, 0)) : 0;

  const downloadCsv = () => {
    if (!data) return;
    const rows = [["Employee", "Week Start", "Week End", "Date", "Day", "Hours"]];
    data.weeks.forEach((w) => {
      const worked = w.days.filter((d) => d.hours > 0);
      if (worked.length === 0) {
        rows.push([data.employee.name, fmtISODate(w.week_start), fmtISODate(w.week_end), "", "", "0"]);
      } else {
        worked.forEach((d) => rows.push([data.employee.name, fmtISODate(w.week_start), fmtISODate(w.week_end), fmtISODate(d.date), d.day, String(d.hours)]));
      }
    });
    rows.push([]);
    rows.push(["Total", "", "", "", "", String(grand)]);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.employee.name.replace(/\s+/g, "_")}_history_${weeks}w.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="employee-history-dialog">
        <DialogHeader>
          <DialogTitle>{emp.name} — Timesheet History</DialogTitle>
          <DialogDescription>Weekly hours (work week Thursday–Wednesday), most recent first.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Show</Label>
            <Select value={String(weeks)} onValueChange={(v) => setWeeks(+v)}>
              <SelectTrigger className="rounded-sm border-slate-300 h-9 w-[130px]" data-testid="history-weeks-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[4, 8, 12, 26].map((n) => <SelectItem key={n} value={String(n)} data-testid={`history-weeks-${n}`}>{n} weeks</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {data && <div className="text-sm text-muted-foreground">Total: <span className="font-mono font-semibold text-foreground">{grand}</span> h</div>}
        </div>
        {loading || !data ? (
          <p className="text-sm text-muted-foreground py-6" data-testid="history-loading">Loading history…</p>
        ) : (
          <div className="max-h-[55vh] overflow-auto divide-y divide-border" data-testid="history-list">
            {data.weeks.map((w) => {
              const worked = w.days.filter((d) => d.hours > 0 || (d.manual && d.entries.length));
              return (
                <div key={w.week_start} className="py-3" data-testid={`history-week-${w.week_start}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{fmtISODate(w.week_start)} → {fmtISODate(w.week_end)}</span>
                    <span className="font-mono font-semibold text-sm" data-testid={`history-total-${w.week_start}`}>{w.total_hours} h</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {worked.length === 0 ? (
                      <span className="text-xs text-muted-foreground">No hours recorded.</span>
                    ) : worked.map((d) => (
                      <Badge key={d.date} variant="outline" className="rounded-sm text-[11px] border-slate-300 bg-slate-50">
                        {d.day} {fmtISODate(d.date).slice(0, 5)}: <span className="font-mono ml-1">{d.hours}</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm border-red-600 text-red-600 hover:bg-red-50 mr-auto" disabled={!data || loading} onClick={downloadCsv} data-testid="history-csv-btn"><Download className="w-4 h-4 mr-1" /> Download CSV</Button>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="history-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Employees() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [historyEmp, setHistoryEmp] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => setList((await api.get("/admin/employees")).data);
  useEffect(() => { load(); }, []);

  const filtered = (query.trim()
    ? list.filter((e) => {
        const q = query.trim().toLowerCase();
        return (e.name || "").toLowerCase().includes(q)
          || (e.first_name || "").toLowerCase().includes(q)
          || (e.last_name || "").toLowerCase().includes(q);
      })
    : list
  ).slice().sort((a, b) =>
    (a.last_name || "").toLowerCase().localeCompare((b.last_name || "").toLowerCase())
    || (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase())
  );

  const del = async () => {
    try {
      await api.delete(`/admin/employees/${delTarget.id}`);
      toast.success("Employee deleted");
      setDelTarget(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="rounded-sm border-border">
      <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="font-heading font-semibold">Employees ({filtered.length}{query.trim() && list.length !== filtered.length ? ` of ${list.length}` : ""})</div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search employees…" className="rounded-sm border-slate-300 h-9 w-[220px] pl-8" data-testid="employee-search-input" />
          </div>
          <Button className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => setEditing({})} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Employee
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Last Name</TableHead>
              <TableHead>First Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Default Start</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6" data-testid="employee-no-match">{list.length === 0 ? "No employees yet." : "No employees match your search."}</TableCell></TableRow>
            )}
            {filtered.map((e) => (
              <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                <TableCell className="font-medium" data-testid={`emp-lastname-${e.id}`}>{e.last_name || "—"}</TableCell>
                <TableCell className="font-medium" data-testid={`emp-firstname-${e.id}`}>{e.first_name || "—"}</TableCell>
                <TableCell>
                  {e.employment_type === "P"
                    ? <Badge className="bg-slate-700 rounded-sm">Permanent</Badge>
                    : <Badge variant="outline" className="border-slate-400 rounded-sm">Casual</Badge>}
                </TableCell>
                <TableCell className="font-mono">${Number(e.hourly_rate).toFixed(2)}</TableCell>
                <TableCell className="font-mono">{e.default_start_time}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setHistoryEmp(e)} data-testid={`history-employee-${e.id}`} title="Timesheet history"><History className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(e)} data-testid={`edit-employee-${e.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDelTarget(e)} data-testid={`delete-employee-${e.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {editing && <EmployeeDialog emp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {historyEmp && <EmployeeHistoryDialog emp={historyEmp} onClose={() => setHistoryEmp(null)} />}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete {delTarget?.name}?</DialogTitle>
            <DialogDescription>This permanently removes the employee. Their past shifts remain in records.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700" onClick={del} data-testid="confirm-delete-employee">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function EmployeeDialog({ emp, onClose, onSaved }) {
  const isEdit = !!emp.id;
  const [form, setForm] = useState({
    first_name: emp.first_name ?? "",
    last_name: emp.last_name ?? "",
    employment_type: emp.employment_type || "P",
    pin: "",
    hourly_rate: emp.hourly_rate ?? 0,
    default_start_time: emp.default_start_time || "07:00",
    weekday_overrides: emp.weekday_overrides || {},
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOv = (d, v) => setForm((f) => {
    const o = { ...f.weekday_overrides };
    if (v) o[d] = v; else delete o[d];
    return { ...f, weekday_overrides: o };
  });

  const save = async () => {
    setErr("");
    if (!form.first_name.trim()) { setErr("First name is required."); toast.error("First name is required"); return; }
    if (!isEdit && !/^\d{4}$/.test(form.pin)) { setErr("A 4-digit PIN is required to add an employee."); toast.error("A 4-digit PIN is required"); return; }
    if (isEdit && form.pin && !/^\d{4}$/.test(form.pin)) { setErr("PIN must be exactly 4 digits."); toast.error("PIN must be 4 digits"); return; }
    setBusy(true);
    try {
      const rate = Number(form.hourly_rate);
      const payload = { ...form, hourly_rate: Number.isFinite(rate) ? rate : 0 };
      if (!payload.pin) delete payload.pin;
      if (isEdit) await api.put(`/admin/employees/${emp.id}`, payload);
      else await api.post(`/admin/employees`, payload);
      toast.success(isEdit ? "Employee updated" : "Employee added");
      onSaved();
    } catch (e) { const m = apiErr(e); setErr(m); toast.error(m); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-auto" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Employee" : "Add Employee"}</DialogTitle>
          <DialogDescription>Set the employee's details, PIN, pay rate and scheduled start times.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm mb-1 block">First name</Label>
              <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="rounded-sm border-slate-300" data-testid="emp-first-name-input" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Last name</Label>
              <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="rounded-sm border-slate-300" data-testid="emp-last-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm mb-1 block">Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => set("employment_type", v)}>
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="emp-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="P">Permanent</SelectItem>
                  <SelectItem value="C">Casual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Hourly rate ($)</Label>
              <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} className="rounded-sm border-slate-300" data-testid="emp-rate-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm mb-1 block">4-digit PIN {isEdit && <span className="text-muted-foreground">(leave blank to keep)</span>}</Label>
              <Input value={form.pin} maxLength={4} inputMode="numeric" onChange={(e) => set("pin", e.target.value.replace(/\D/g, ""))} className="rounded-sm border-slate-300 font-mono" data-testid="emp-pin-input" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Default start time</Label>
              <Input type="time" value={form.default_start_time} onChange={(e) => set("default_start_time", e.target.value)} className="rounded-sm border-slate-300" data-testid="emp-start-input" />
            </div>
          </div>
          <div>
            <Label className="text-sm mb-2 block">Per-weekday start overrides (optional)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DAYS.map((d) => (
                <div key={d}>
                  <div className="text-xs uppercase text-muted-foreground mb-1">{d}</div>
                  <Input type="time" value={form.weekday_overrides[d] || ""} onChange={(e) => setOv(d, e.target.value)} className="rounded-sm border-slate-300 h-9 text-sm" data-testid={`emp-override-${d}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
        {err && <p className="text-sm text-red-600 font-medium" data-testid="emp-error">{err}</p>}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={busy} onClick={save} data-testid="save-employee-btn">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Approvals ---------------- */
function Approvals({ early, forgot, reload }) {
  const decideEarly = async (id, decision) => {
    try { await api.post(`/admin/early-starts/${id}/${decision}`); toast.success(`Early start ${decision}`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const decideForgot = async (id, decision) => {
    try { await api.post(`/admin/forgot-requests/${id}/${decision}`); toast.success(`Request ${decision}d`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Staff requests appear here. Refreshes automatically every 20s.</p>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={reload} data-testid="approvals-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
      <Card className="rounded-sm border-border">
        <div className="p-4 border-b border-border font-heading font-semibold">Early Start Approvals ({early.length})</div>
        <div className="divide-y divide-border">
          {early.length === 0 && <div className="p-6 text-center text-muted-foreground">No pending early starts.</div>}
          {early.map((e) => (
            <div key={e.shift_id} className="p-4 flex items-center justify-between" data-testid={`early-row-${e.shift_id}`}>
              <div>
                <div className="font-medium">{e.name}</div>
                <div className="text-sm text-muted-foreground font-mono">In {fmtTime(e.clock_in)} · Scheduled {e.scheduled_start || "—"}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" onClick={() => decideEarly(e.shift_id, "approved")} data-testid={`early-approve-${e.shift_id}`}><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => decideEarly(e.shift_id, "rejected")} data-testid={`early-reject-${e.shift_id}`}><X className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-sm border-border">
        <div className="p-4 border-b border-border font-heading font-semibold">Forgot Clock In/Out Requests ({forgot.length})</div>
        <div className="divide-y divide-border">
          {forgot.length === 0 && <div className="p-6 text-center text-muted-foreground">No pending requests.</div>}
          {forgot.map((r) => (
            <div key={r.id} className="p-4 flex items-start justify-between gap-3" data-testid={`forgot-row-${r.id}`}>
              <div className="min-w-0">
                <div className="font-medium">{r.employee_name} <Badge variant="outline" className="rounded-sm ml-1">{r.type === "in" ? "Clock In" : "Clock Out"}</Badge></div>
                <div className="text-sm text-muted-foreground font-mono">{fmtDateTime(r.submitted_time)}</div>
                {r.reason && <div className="text-sm text-foreground mt-1">Reason: <span className="text-muted-foreground">{r.reason}</span></div>}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" className="rounded-sm bg-green-600 hover:bg-green-700" onClick={() => decideForgot(r.id, "approve")} data-testid={`forgot-approve-${r.id}`}><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" className="rounded-sm text-red-600" onClick={() => decideForgot(r.id, "reject")} data-testid={`forgot-reject-${r.id}`}><X className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
      </div>
    </div>
  );
}

/* ---------------- Flags & Alerts ---------------- */
function Flags({ late, alerts, blocked, pinLockouts = [], reload }) {
  const dismiss = async (url, label) => {
    try { await api.post(url); toast.success(label); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const clearAll = async (url, label, count) => {
    if (!count) return;
    try { const { data } = await api.post(url); toast.success(`${label} (${data.cleared} cleared)`); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const ClearAllBtn = ({ onClick, count, testid }) => (
    count > 0 ? (
      <Button size="sm" variant="ghost" className="rounded-sm h-7 text-xs text-slate-500 hover:text-red-600 ml-auto" onClick={onClick} data-testid={testid}>
        <X className="w-3.5 h-3.5 mr-1" /> Clear All
      </Button>
    ) : null
  );
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="rounded-sm border-border">
        <div className="p-4 border-b border-border font-heading font-semibold flex items-center gap-2">
          <Flag className="w-4 h-4 text-amber-500" /> Late Arrivals ({late.length})
          <ClearAllBtn count={late.length} testid="clear-all-late" onClick={() => clearAll("/admin/late-arrivals/dismiss-all", "Late arrivals cleared", late.length)} />
        </div>
        <div className="divide-y divide-border max-h-[26rem] overflow-auto">
          {late.length === 0 && <div className="p-6 text-center text-muted-foreground">No late arrivals.</div>}
          {late.map((l) => (
            <LateArrivalRow key={l.shift_id} l={l} onDismiss={(id) => dismiss(`/admin/late-arrivals/${id}/dismiss`, "Late arrival cleared")} />
          ))}
        </div>
      </Card>
      <Card className="rounded-sm border-border">
        <div className="p-4 border-b border-border font-heading font-semibold flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" /> Missed Clock-out Alerts ({alerts.length})
          <ClearAllBtn count={alerts.length} testid="clear-all-alerts" onClick={() => clearAll("/admin/alerts/dismiss-all", "Alerts cleared", alerts.length)} />
        </div>
        <div className="divide-y divide-border max-h-[26rem] overflow-auto">
          {alerts.length === 0 && <div className="p-6 text-center text-muted-foreground">No auto clock-outs.</div>}
          {alerts.map((a) => (
            <div key={a.shift_id} className="p-4 flex items-center justify-between" data-testid={`alert-row-${a.shift_id}`}>
              <div>
                <div className="font-medium">{a.name} {a.reviewed && <Badge className="bg-green-600 rounded-sm ml-1">Reviewed</Badge>}</div>
                <div className="text-sm text-muted-foreground font-mono">Auto out {fmtDateTime(a.clock_out)}</div>
              </div>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => dismiss(`/admin/alerts/${a.shift_id}/dismiss`, "Alert cleared")} data-testid={`clear-alert-${a.shift_id}`}>
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
          ))}
        </div>
      </Card>
      <Card className="rounded-sm border-border lg:col-span-2">
        <div className="p-4 border-b border-border font-heading font-semibold flex items-center gap-2">
          <MapPin className="w-4 h-4 text-red-600" /> Blocked Clock-in Attempts ({blocked.length})
          <ClearAllBtn count={blocked.length} testid="clear-all-blocked" onClick={() => clearAll("/admin/blocked-attempts/dismiss-all", "Blocked attempts cleared", blocked.length)} />
        </div>
        <div className="divide-y divide-border max-h-[26rem] overflow-auto">
          {blocked.length === 0 && <div className="p-6 text-center text-muted-foreground">No blocked attempts — everyone has clocked in within their geofence.</div>}
          {blocked.map((b) => (
            <div key={b.id} className="p-4 flex items-center justify-between gap-3" data-testid={`blocked-row-${b.id}`}>
              <div>
                <div className="font-medium">{b.employee_name} <Badge variant="outline" className="rounded-sm ml-1">{b.location_name}</Badge></div>
                <div className="text-sm text-muted-foreground font-mono">
                  {fmtDateTime(b.timestamp)} · {b.reason === "no_gps" ? "No GPS / location denied" : `~${b.distance_m}m away (limit ${b.radius_m}m)`}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className="bg-red-600 rounded-sm">Blocked</Badge>
                <Button size="sm" variant="ghost" className="rounded-sm h-8 text-slate-500 hover:text-red-600" onClick={() => dismiss(`/admin/blocked-attempts/${b.id}/dismiss`, "Blocked attempt cleared")} data-testid={`clear-blocked-${b.id}`}>
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Card className="rounded-sm border-border lg:col-span-2">
        <div className="p-4 border-b border-border font-heading font-semibold flex items-center gap-2">
          <Lock className="w-4 h-4 text-red-600" /> PIN Lockouts ({pinLockouts.length})
          <ClearAllBtn count={pinLockouts.length} testid="clear-all-lockouts" onClick={() => clearAll("/admin/pin-lockouts/dismiss-all", "PIN lockouts cleared", pinLockouts.length)} />
        </div>
        <div className="divide-y divide-border max-h-[26rem] overflow-auto">
          {pinLockouts.length === 0 && <div className="p-6 text-center text-muted-foreground">No PIN lockouts — no employees have been locked out.</div>}
          {pinLockouts.map((p) => (
            <div key={p.id} className="p-4 flex items-center justify-between gap-3" data-testid={`lockout-row-${p.id}`}>
              <div>
                <div className="font-medium">{p.employee_name}
                  {p.active
                    ? <Badge className="bg-red-600 hover:bg-red-600 rounded-sm ml-2">Locked</Badge>
                    : <Badge variant="outline" className="rounded-sm ml-2 border-green-600 text-green-700">Expired</Badge>}
                </div>
                <div className="text-sm text-muted-foreground font-mono">
                  Locked {fmtDateTime(p.locked_at)}{p.locked_until ? ` · until ${fmtDateTime(p.locked_until)}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.active && (
                  <Button size="sm" variant="outline" className="rounded-sm border-red-600 text-red-600 hover:bg-red-50" onClick={() => dismiss(`/admin/pin-lockouts/${p.id}/unlock`, `${p.employee_name} unlocked`)} data-testid={`unlock-${p.id}`}>
                    <Unlock className="w-4 h-4 mr-1" /> Unlock Now
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-sm h-8 text-slate-500 hover:text-red-600" onClick={() => dismiss(`/admin/pin-lockouts/${p.id}/dismiss`, "Lockout cleared")} data-testid={`clear-lockout-${p.id}`}>
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
function Timesheets() {
  const [weekStart, setWeekStart] = useState(null);
  const [data, setData] = useState(null);
  const [editShift, setEditShift] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [leaveCell, setLeaveCell] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [lockTarget, setLockTarget] = useState(null);
  const [lockPinConfigured, setLockPinConfigured] = useState(true);
  const [fillTarget, setFillTarget] = useState(null);
  const [filling, setFilling] = useState(false);
  const [catList, setCatList] = useState(CATEGORIES);

  const load = async (ws) => {
    const params = ws ? { week_start: ws } : {};
    const { data } = await api.get("/admin/weekly", { params });
    setData(data);
    setWeekStart(data.week_start);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/admin/settings").then((r) => setLockPinConfigured(!!r.data.timesheet_lock_pin_configured)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/categories").then((r) => setCatList(r.data)).catch(() => {}); }, []);

  const handleLockToggle = (r) => {
    if (!lockPinConfigured) { toast.error("Set a Timesheet Lock PIN in Settings before locking timesheets."); return; }
    setLockTarget({ emp: r, lock: !r.locked });
  };

  const doQuickFill = async () => {
    if (!fillTarget) return;
    setFilling(true);
    try {
      const { data } = await api.post("/admin/timesheet/quick-fill", { employee_id: fillTarget.employee_id, week_start: weekStart });
      toast.success(`Standard hours filled for ${fillTarget.first_name || fillTarget.name}`, { description: `${data.filled} days set (Mon–Thu 8h, Fri 6h)` });
      setFillTarget(null);
      load(weekStart);
    } catch (e) { toast.error(apiErr(e)); } finally { setFilling(false); }
  };

  const step = (days) => {
    const d = new Date(weekStart + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    load(d.toISOString().slice(0, 10));
  };

  const downloadFile = async (fmt) => {
    try {
      const res = await api.get(`/admin/weekly/${fmt}`, { params: { week_start: weekStart }, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename=([^;]+)/);
      const filename = match ? match[1].trim() : `TimesheetResults.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const headers = data.day_headers;
  const wantType = typeFilter === "permanent" ? "P" : typeFilter === "casual" ? "C" : null;
  const rows = wantType ? data.rows.filter((r) => r.employment_type === wantType) : data.rows;
  const dayTotals = headers.map((h) =>
    +rows.reduce((sum, r) => {
      const cell = r.days.find((d) => d.date === h.date);
      return sum + (cell ? cell.hours : 0);
    }, 0).toFixed(2)
  );
  const grandTotal = +rows.reduce((s, r) => s + r.total_hours, 0).toFixed(2);
  const allLocked = rows.length > 0 && rows.every((r) => r.locked);
  const apiCategoryTotals = data.totals?.category_totals || {};
  const catTotals = {};
  let uncategorised = 0;
  catList.forEach((c) => {
    catTotals[c.value] = typeFilter === "all"
      ? +(apiCategoryTotals[c.value] || 0).toFixed(2)
      : 0;
  });
  if (typeFilter === "all") {
    uncategorised = +(data.totals?.uncategorised || 0).toFixed(2);
  } else {
    rows.forEach((r) => r.days.forEach((d) => {
      const entries = d.payroll_entries || (d.manual ? d.entries : []);
      if (entries.length === 0) {
        uncategorised += d.hours;
        return;
      }
      entries.forEach((entry) => {
        catTotals[entry.category] = +(
          (catTotals[entry.category] || 0) + entry.hours
        ).toFixed(2);
      });
    }));
    uncategorised = +uncategorised.toFixed(2);
  }
  const activeCats = catList.filter((c) => catTotals[c.value] > 0);

  return (
    <Card className="rounded-sm border-border">
      <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => step(-7)} data-testid="week-prev"><ChevronLeft className="w-4 h-4 mr-1" /> Prev</Button>
          <div className="font-heading font-semibold text-sm text-center min-w-[190px]" data-testid="timesheet-week-label">Week {fmtISODate(data.week_start)} → {fmtISODate(data.week_end)}</div>
          {allLocked && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[11px] font-semibold px-2.5 py-1" data-testid="week-locked-chip">
              <Lock className="w-3 h-3" /> Locked
            </span>
          )}
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => step(7)} data-testid="week-next">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
          <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => load()} data-testid="week-this">This Week</Button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="rounded-sm border-slate-300 h-9 w-[150px]" data-testid="timesheet-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="filter-all">All employees</SelectItem>
              <SelectItem value="permanent" data-testid="filter-permanent">Permanent only</SelectItem>
              <SelectItem value="casual" data-testid="filter-casual">Casual only</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className={`rounded-sm ${allLocked ? "border-green-600 text-green-700 hover:bg-green-50" : "border-red-600 text-red-600 hover:bg-red-50"}`} onClick={() => { if (!lockPinConfigured) { toast.error("Set a Timesheet Lock PIN in Settings before locking timesheets."); return; } setLockTarget({ bulk: true, lock: !allLocked }); }} data-testid="lock-all-btn">
            {allLocked ? <><Unlock className="w-4 h-4 mr-1" /> Unlock All</> : <><Lock className="w-4 h-4 mr-1" /> Lock All</>}
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditShift(true)} data-testid="edit-shifts-btn"><Pencil className="w-4 h-4 mr-1" /> Edit Times</Button>
          <Button size="sm" className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => downloadFile("csv")} data-testid="csv-export-btn"><Download className="w-4 h-4 mr-1" /> CSV</Button>
          <Button size="sm" variant="outline" className="rounded-sm border-red-600 text-red-600 hover:bg-red-50" onClick={() => downloadFile("txt")} data-testid="txt-export-btn"><Download className="w-4 h-4 mr-1" /> TXT</Button>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => downloadFile("pdf")} data-testid="pdf-export-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setShowExport(true)} data-testid="custom-export-btn"><CalendarRange className="w-4 h-4 mr-1" /> Custom Range</Button>
        </div>
      </div>
      <p className="px-4 pt-3 text-xs text-muted-foreground">Tip: click any day cell to enter payroll categories (Base Hourly, Overtime, leave, etc.) with hours for that day.</p>
      <div className="overflow-x-auto">
        <Table className="w-full table-fixed text-[10px] sm:table-auto sm:text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-background">Last Name</TableHead>
              <TableHead>First Name</TableHead>
              <TableHead>Type</TableHead>
              {headers.map((h) => (
                <TableHead key={h.date} className="text-center whitespace-nowrap">
                  {h.day}<br /><span className="font-normal text-xs text-muted-foreground">{fmtISODate(h.date).slice(0, 5)}</span>
                </TableHead>
              ))}
              <TableHead className="text-right">Total Hrs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={headers.length + 4} className="text-center text-muted-foreground py-6" data-testid="timesheet-no-match">No employees match this filter.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.employee_id} data-testid={`timesheet-row-${r.employee_id}`} className={r.locked ? "bg-red-50/60" : undefined}>
                <TableCell className="font-medium sticky left-0 bg-background break-words" data-testid={`ts-lastname-${r.employee_id}`}>{r.last_name || "—"}</TableCell>
                <TableCell className="font-medium" data-testid={`ts-firstname-${r.employee_id}`}>
                  <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
                    {r.first_name || "—"}
                    {r.locked
                      ? <Lock className="w-3.5 h-3.5 text-red-600 shrink-0" data-testid={`lock-icon-${r.employee_id}`} />
                      : <Unlock className="w-3.5 h-3.5 text-slate-400 shrink-0" data-testid={`unlock-icon-${r.employee_id}`} />}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleLockToggle(r); }}
                      className={`text-[10px] font-semibold hover:underline ${r.locked ? "text-green-700" : "text-red-600"}`}
                      data-testid={`lock-toggle-${r.employee_id}`}
                    >{r.locked ? "Unlock" : "Lock"}</button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); if (r.locked) { toast.error("Timesheet is locked — unlock to make changes."); return; } setFillTarget(r); }}
                      className="text-[10px] font-semibold text-slate-600 hover:text-red-600 hover:underline"
                      data-testid={`quickfill-${r.employee_id}`}
                    >Fill Std</button>
                    {r.currently_clocked_in && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 text-[10px] font-semibold px-2 py-0.5" title={`On site since ${r.clocked_in_since}`} data-testid={`clocked-in-${r.employee_id}`}>
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"></span>
                        </span>
                        On site · {r.clocked_in_since}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{r.employment_type === "P" ? "P" : "C"}</TableCell>
                {r.days.map((d) => (
                  <TableCell
                    key={d.date}
                    className={`text-center transition-colors p-1 align-middle ${r.locked ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-red-50"} ${d.public_holiday ? "bg-amber-50 ring-1 ring-inset ring-amber-300" : ""}`}
                    onClick={() => { if (r.locked) { toast.error("Timesheet is locked — unlock to make changes."); return; } setLeaveCell({ emp: r, day: d }); }}
                    data-testid={`ts-cell-${r.employee_id}-${d.date}`}
                    title={d.public_holiday ? `Public holiday: ${d.public_holiday_name}` : undefined}
                  >
                    {d.public_holiday && (
                      <div className="text-[9px] font-bold uppercase tracking-wide text-amber-700 mb-0.5 truncate" data-testid={`ph-tag-${r.employee_id}-${d.date}`}>PH</div>
                    )}
                    {d.manual && d.entries.length > 0 ? (
                      <div className="flex flex-col items-center gap-0.5">
                        {d.entries.map((it, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="rounded-sm text-[10px] leading-tight whitespace-nowrap border-slate-400 bg-slate-50"
                          >
                            {CAT_SHORT[it.category] || it.label}: {it.hours}
                          </Badge>
                        ))}
                      </div>
                    ) : d.hours > 0 ? (
                      <span className="font-mono text-sm">{d.hours}</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">–</span>
                    )}
                  </TableCell>
                ))}
                <TableCell className="text-right font-mono font-semibold">{r.total_hours}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted font-semibold" data-testid="timesheet-total-row">
              <TableCell colSpan={3} className="sticky left-0 bg-muted">TOTAL</TableCell>
              {dayTotals.map((t, i) => (
                <TableCell key={i} className="text-center font-mono">{t}</TableCell>
              ))}
              <TableCell className="text-right font-mono">{grandTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      {(activeCats.length > 0 || uncategorised > 0) && (
        <div className="p-4 border-t border-border" data-testid="category-summary">
          <h4 className="font-heading font-semibold text-sm mb-2">Category Totals (whole week)</h4>
          <div className="flex flex-wrap gap-2">
            {activeCats.map((c) => (
              <div key={c.value} className="rounded-sm border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm" data-testid={`cat-total-${c.value}`}>
                <span className="text-muted-foreground">{c.label}:</span> <span className="font-mono font-semibold">{catTotals[c.value]}</span> hrs
              </div>
            ))}
            {uncategorised > 0 && (
              <div className="rounded-sm border border-slate-300 bg-slate-50 px-3 py-1.5 text-sm" data-testid="cat-total-clocked">
                <span className="text-muted-foreground">Clocked (uncategorised):</span> <span className="font-mono font-semibold">{uncategorised}</span> hrs
              </div>
            )}
            <div className="rounded-sm border border-red-300 bg-red-50 px-3 py-1.5 text-sm" data-testid="cat-total-grand">
              <span className="text-muted-foreground">Total:</span> <span className="font-mono font-semibold">{grandTotal}</span> hrs
            </div>
          </div>
        </div>
      )}
      {editShift && <EditShiftsDialog onClose={() => setEditShift(null)} />}
      {showExport && <CustomExportDialog onClose={() => setShowExport(false)} />}
      {leaveCell && (
        <DayLeaveDialog
          emp={leaveCell.emp}
          day={leaveCell.day}
          onClose={() => setLeaveCell(null)}
          onSaved={() => { setLeaveCell(null); load(weekStart); }}
        />
      )}
      {lockTarget && (
        <TimesheetLockDialog
          emp={lockTarget.emp}
          bulk={lockTarget.bulk}
          lock={lockTarget.lock}
          weekStart={weekStart}
          onClose={() => setLockTarget(null)}
          onDone={() => { setLockTarget(null); load(weekStart); }}
        />
      )}
      {fillTarget && (
        <Dialog open onOpenChange={() => setFillTarget(null)}>
          <DialogContent className="rounded-sm max-w-sm" data-testid="quickfill-dialog">
            <DialogHeader>
              <DialogTitle>Fill standard hours?</DialogTitle>
              <DialogDescription>
                This will set <span className="font-medium">{fillTarget.first_name} {fillTarget.last_name}</span>'s week to standard <span className="font-medium">Base Hourly</span> hours: <span className="font-medium">8h Monday–Thursday</span> and <span className="font-medium">6h Friday</span>. Saturday and Sunday are left empty. Any existing entries on those weekdays will be overwritten. You can still adjust any cell afterwards.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setFillTarget(null)} data-testid="quickfill-cancel">Cancel</Button>
              <Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={filling} onClick={doQuickFill} data-testid="quickfill-confirm">{filling ? "Filling…" : "Fill Standard Hours"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

function TimesheetLockDialog({ emp, bulk, lock, weekStart, onClose, onDone }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("Enter the 4-digit Timesheet Lock PIN");
    setBusy(true);
    try {
      if (bulk) {
        await api.post("/admin/timesheet-lock-all", { week_start: weekStart, lock, pin });
        toast.success(lock ? "All timesheets locked" : "All timesheets unlocked");
      } else {
        await api.post("/admin/timesheet-lock", { employee_id: emp.employee_id, week_start: weekStart, lock, pin });
        toast.success(lock ? "Timesheet locked" : "Timesheet unlocked");
      }
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const title = bulk ? (lock ? "Lock All Timesheets" : "Unlock All Timesheets") : (lock ? "Lock Timesheet" : "Unlock Timesheet");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-sm" data-testid="timesheet-lock-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {bulk
              ? <>{lock ? "Locking" : "Unlocking"} <span className="font-medium">every employee's</span> timesheet for this week. Enter the Timesheet Lock PIN to confirm.</>
              : <>{lock ? "Locking" : "Unlocking"} <span className="font-medium">{emp.first_name} {emp.last_name}</span>'s timesheet for this week. Enter the Timesheet Lock PIN to confirm.</>}
          </DialogDescription>
        </DialogHeader>
        <Input value={pin} maxLength={4} inputMode="numeric" type="password" onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="Timesheet Lock PIN" className="rounded-sm border-slate-300 font-mono" data-testid="lock-pin-input" autoFocus />
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="lock-cancel">Cancel</Button>
          <Button className={`rounded-sm ${lock ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}`} disabled={busy} onClick={submit} data-testid="lock-confirm">{lock ? "Lock" : "Unlock"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomExportDialog({ onClose }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async (fmt) => {
    if (!start || !end) return toast.error("Pick a start and end date.");
    setBusy(true);
    try {
      const res = await api.get(`/admin/export/${fmt}`, { params: { start, end }, responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename=([^;]+)/);
      const filename = m ? m[1].trim() : `TimesheetResults.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const emailIt = async () => {
    if (!start || !end) return toast.error("Pick a start and end date.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/export/email`, null, { params: { start, end } });
      toast.success(`Emailed ${data.line_items} line items to ${data.recipient}`);
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle>Custom Payroll Export</DialogTitle>
          <DialogDescription>Pick any date range (e.g. a fortnight or full pay period) to download or email the payroll file. Dates are inclusive.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 py-1">
          <div className="flex-1">
            <Label className="text-sm mb-1 block">Start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-sm border-slate-300 h-9" data-testid="export-start" />
          </div>
          <div className="flex-1">
            <Label className="text-sm mb-1 block">End date</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-sm border-slate-300 h-9" data-testid="export-end" />
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" className="rounded-sm" disabled={busy} onClick={() => download("csv")} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" /> CSV</Button>
          <Button variant="outline" className="rounded-sm" disabled={busy} onClick={() => download("txt")} data-testid="export-txt-btn"><Download className="w-4 h-4 mr-1" /> TXT</Button>
          <Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={busy} onClick={emailIt} data-testid="export-email-btn"><Mail className="w-4 h-4 mr-1" /> Email files</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DayLeaveDialog({ emp, day, onClose, onSaved }) {
  const WORK_CATS = ["base", "ot15", "ot2"];
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState(CATEGORIES);

  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/day-detail", { params: { employee_id: emp.employee_id, date: day.date } });
        setDetail(data);
        if (data.entries && data.entries.length) {
          setEntries(data.entries.map((e) => ({ category: e.category, hours: String(e.hours) })));
        } else if (data.payable_hours > 0) {
          const s = data.split || {};
          const rows = [];
          if (s.base > 0) rows.push({ category: "base", hours: String(s.base) });
          if (s.ot15 > 0) rows.push({ category: "ot15", hours: String(s.ot15) });
          if (s.ot2 > 0) rows.push({ category: "ot2", hours: String(s.ot2) });
          setEntries(rows.length ? rows : [{ category: "base", hours: String(data.payable_hours) }]);
        } else {
          setEntries([]);
        }
      } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
    })();
  }, [emp.employee_id, day.date]);

  const [applyBreak, setApplyBreak] = useState(true);
  const addRow = () => setEntries((es) => [...es, { category: "base", hours: "" }]);
  const removeRow = (i) => setEntries((es) => es.filter((_, idx) => idx !== i));
  const setRow = (i, key, val) => setEntries((es) => es.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));

  const clocked = detail ? detail.clocked_hours : 0;
  const noRecords = detail && detail.shift_count === 0;
  const breakAmt = detail ? (detail.unpaid_break || 0) : 0;
  const payable = round2(clocked - (applyBreak ? breakAmt : 0));

  const autoSplit = () => {
    const s = splitHours(payable, detail.weekday);
    const rows = [];
    if (s.base > 0) rows.push({ category: "base", hours: String(s.base) });
    if (s.ot15 > 0) rows.push({ category: "ot15", hours: String(s.ot15) });
    if (s.ot2 > 0) rows.push({ category: "ot2", hours: String(s.ot2) });
    if (!rows.length && payable > 0) rows.push({ category: "base", hours: String(payable) });
    setEntries(rows);
    toast.success("Split into Base + Overtime using pay rules");
  };

  const pastWeek = detail ? !!detail.past_week : false;
  const total = entries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0);
  const workedTotal = entries.reduce((s, e) => s + (WORK_CATS.includes(e.category) ? (parseFloat(e.hours) || 0) : 0), 0);
  const overAllocated = !pastWeek && payable > 0 && workedTotal - payable > 0.01;

  const save = async () => {
    for (const e of entries) {
      if (e.hours === "" || isNaN(parseFloat(e.hours))) return toast.error("Enter hours for every category row.");
    }
    if (overAllocated) return toast.error(`Worked hours (${workedTotal.toFixed(2)}) exceed the ${payable}h payable for this day.`);
    setBusy(true);
    try {
      await api.post("/admin/day-entries", {
        employee_id: emp.employee_id,
        date: day.date,
        entries: entries.map((e) => ({ category: e.category, hours: parseFloat(e.hours) })),
      });
      toast.success(entries.length ? "Day updated" : "Reverted to clocked hours");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm" data-testid="leave-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">{emp.name} — {day.day} {fmtISODate(day.date)}
            {detail?.public_holiday && <Badge className="bg-amber-500 hover:bg-amber-500 rounded-sm text-[10px]" data-testid="day-ph-badge">Public Holiday: {detail.public_holiday_name}</Badge>}
          </DialogTitle>
          <DialogDescription>
            Split the clocked hours into payroll categories (Base Hourly, Overtime, leave, etc.). Worked-category hours can't exceed the actual clocked hours; leave categories are separate.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="day-loading">Loading clocked hours…</p>
        ) : (
          <>
            <div className="rounded-sm bg-slate-50 border border-slate-200 px-3 py-2 mb-1" data-testid="day-clocked-summary">
              {noRecords ? (
                <p className="text-sm text-amber-700" data-testid="day-no-records">No clock-in/out records found for this date.</p>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm">
                      Clocked: <span className="font-mono font-semibold">{clocked}</span> h
                      {breakAmt > 0 && applyBreak && <span className="text-muted-foreground"> − {breakAmt} break = </span>}
                      {breakAmt > 0 && applyBreak && <span className="font-mono font-semibold">{payable}</span>}
                      {breakAmt > 0 && applyBreak && <span className="text-muted-foreground"> h payable</span>}
                      {detail.open_count > 0 && <span className="text-amber-700"> · {detail.open_count} shift still open</span>}
                    </p>
                    {clocked > 0 && (
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={autoSplit} data-testid="auto-split-btn">
                        Auto-split Base + OT
                      </Button>
                    )}
                  </div>
                  {breakAmt > 0 && (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="break-override-label">
                      <input type="checkbox" checked={applyBreak} onChange={(e) => setApplyBreak(e.target.checked)} data-testid="break-override-checkbox" />
                      Deduct 30-min unpaid meal break (worked &gt; 6h). Untick to override.
                    </label>
                  )}
                </div>
              )}
            </div>

            {detail.shifts && detail.shifts.length > 0 && (
              <div className="rounded-sm border border-slate-200 px-3 py-2 mb-1 space-y-1.5" data-testid="day-shifts-detail">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Clock Records</div>
                {detail.shifts.map((s, i) => (
                  <div key={i} className="flex items-center flex-wrap gap-x-2 gap-y-1 text-sm" data-testid={`day-shift-${i}`}>
                    <span className="font-mono">In {s.clock_in}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-mono">{s.clock_out ? `Out ${s.clock_out}` : "still open"}</span>
                    {s.hours != null && <span className="text-muted-foreground">· {s.hours} h</span>}
                    {s.scheduled_start && <span className="text-xs text-muted-foreground">(scheduled {s.scheduled_start})</span>}
                    {s.paid_from_scheduled && <Badge variant="outline" className="rounded-sm text-[10px] border-slate-400 text-slate-600" data-testid={`flag-paid-sched-${i}`}>Paid from scheduled start</Badge>}
                    {s.early_start === "pending" && <Badge className="bg-amber-500 hover:bg-amber-500 rounded-sm text-[10px]" data-testid={`flag-early-${i}`}>Early start · pending</Badge>}
                    {s.early_start === "approved" && <Badge className="bg-green-600 hover:bg-green-600 rounded-sm text-[10px]" data-testid={`flag-early-${i}`}>Early start · approved</Badge>}
                    {s.late_arrival && <Badge className="bg-amber-600 hover:bg-amber-600 rounded-sm text-[10px]" data-testid={`flag-late-${i}`}>Late arrival</Badge>}
                    {s.auto_clock_out && <Badge className="bg-red-600 hover:bg-red-600 rounded-sm text-[10px]" data-testid={`flag-missed-${i}`}>Missed clock-out · auto</Badge>}
                    {!s.clock_out && <Badge variant="outline" className="rounded-sm text-[10px] border-green-600 text-green-700" data-testid={`flag-open-${i}`}>Open</Badge>}
                  </div>
                ))}
                {detail.shifts.filter((s) => s.late_note).map((s, i) => (
                  <div key={`note-${i}`} className="text-xs text-muted-foreground" data-testid={`day-late-note-${i}`}>Late note: {s.late_note}</div>
                ))}
              </div>
            )}
            <div className="space-y-2 py-1 max-h-[45vh] overflow-y-auto">
              {entries.map((e, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`day-entry-row-${i}`}>
                  <Select value={e.category} onValueChange={(v) => setRow(i, "category", v)}>
                    <SelectTrigger className="rounded-sm border-slate-300 h-9 flex-1" data-testid={`day-cat-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {cats.map((c) => (
                        <SelectItem key={c.value} value={c.value} data-testid={`cat-opt-${c.value}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number" step="0.25" min="0" placeholder="Hrs"
                    value={e.hours}
                    onChange={(ev) => setRow(i, "hours", ev.target.value)}
                    className="rounded-sm border-slate-300 h-9 w-24"
                    data-testid={`day-hrs-${i}`}
                  />
                  <Button size="icon" variant="ghost" className="rounded-sm h-9 w-9 text-red-600" onClick={() => removeRow(i)} data-testid={`day-remove-${i}`}><X className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="rounded-sm w-full" onClick={addRow} data-testid="day-add-category">
                <Plus className="w-4 h-4 mr-1" /> Add category
              </Button>
              {entries.length > 0 && (
                <div className="text-sm text-right pt-1 space-y-0.5">
                  <p className={overAllocated ? "text-red-600 font-semibold" : "font-medium"} data-testid="day-worked-total">
                    Worked allocated: <span className="font-mono">{workedTotal.toFixed(2)}</span> / {payable} h
                    {overAllocated && " — exceeds payable!"}
                  </p>
                  {pastWeek && (
                    <p className="text-xs text-muted-foreground" data-testid="day-pastweek-note">Past week — manual hours not capped to clocked time.</p>
                  )}
                  <p className="text-muted-foreground">Day total (incl. leave): <span className="font-mono">{total.toFixed(2)}</span> h</p>
                </div>
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="leave-cancel">Cancel</Button>
          <Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={busy || loading || overAllocated} onClick={save} data-testid="leave-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditShiftsDialog({ onClose }) {
  const [shifts, setShifts] = useState([]);
  const [target, setTarget] = useState(null);
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/shifts").then((r) => setShifts(r.data)); }, []);

  const openEdit = (s) => { setTarget(s); setCi(toLocalInput(s.clock_in)); setCo(toLocalInput(s.clock_out)); setPw(""); };

  const save = async () => {
    if (!pw) return toast.error("Admin password required to confirm");
    setBusy(true);
    try {
      await api.put(`/admin/shifts/${target.shift_id}`, { admin_password: pw, clock_in: ci || null, clock_out: co || null });
      toast.success("Shift updated & logged to audit");
      const r = await api.get("/admin/shifts"); setShifts(r.data); setTarget(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto" data-testid="edit-shifts-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manually Edit Clock Times</DialogTitle>
          <DialogDescription>Editing requires admin password confirmation and is recorded in the audit log.</DialogDescription>
        </DialogHeader>
        {!target ? (
          <div className="max-h-96 overflow-auto divide-y divide-border">
            {shifts.map((s) => (
              <button key={s.shift_id} onClick={() => openEdit(s)} className="w-full text-left p-3 hover:bg-slate-50 flex justify-between items-center" data-testid={`shift-item-${s.shift_id}`}>
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{fmtDateTime(s.clock_in)} → {s.clock_out ? fmtDateTime(s.clock_out) : "open"}</div>
                </div>
                <Pencil className="w-4 h-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="font-medium">{target.name}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm mb-1 block">Clock In (AEST)</Label><Input type="datetime-local" value={ci} onChange={(e) => setCi(e.target.value)} className="rounded-sm border-slate-300" data-testid="edit-ci" /></div>
              <div><Label className="text-sm mb-1 block">Clock Out (AEST)</Label><Input type="datetime-local" value={co} onChange={(e) => setCo(e.target.value)} className="rounded-sm border-slate-300" data-testid="edit-co" /></div>
            </div>
            <div><Label className="text-sm mb-1 block">Confirm admin password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-sm border-slate-300" data-testid="edit-pw" /></div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setTarget(null)}>Back</Button>
              <Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={busy} onClick={save} data-testid="save-shift-edit">Save changes</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Audit ---------------- */
function AuditLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-log").then((r) => setLogs(r.data)); }, []);
  return (
    <Card className="rounded-sm border-border">
      <div className="p-4 border-b border-border font-heading font-semibold">Audit Log ({logs.length})</div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Action</TableHead><TableHead>Detail</TableHead><TableHead>Admin</TableHead></TableRow></TableHeader>
          <TableBody>
            {logs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No entries.</TableCell></TableRow>}
            {logs.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-mono text-xs whitespace-nowrap">{fmtDateTime(l.timestamp)}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm">{l.action}</Badge></TableCell>
                <TableCell className="text-sm">{l.detail}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.admin}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

/* ---------------- QR Code Manager ---------------- */
function QrManager() {
  const [list, setList] = useState([]);
  const [dialog, setDialog] = useState(null); // {mode:'add'} | {mode:'edit', loc}
  const [delTarget, setDelTarget] = useState(null);
  const refs = useRef({});
  const origin = window.location.origin;

  const load = async () => setList((await api.get("/admin/locations")).data);
  useEffect(() => { load(); }, []);

  const del = async () => {
    try { await api.delete(`/admin/locations/${delTarget.id}`); setDelTarget(null); load(); toast.success("Location deleted"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const getCanvas = (id) => refs.current[id]?.querySelector("canvas");
  const safe = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  const downloadPng = (loc) => {
    const c = getCanvas(loc.id);
    if (!c) return toast.error("QR not ready yet");
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `rosemount-qr-${safe(loc.name)}.png`;
    a.click();
  };
  const downloadPdf = (loc) => {
    const c = getCanvas(loc.id);
    if (!c) return toast.error("QR not ready yet");
    const img = c.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(24); pdf.text("Rosemount Joinery", pw / 2, 90, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(15); pdf.text("Staff Clock-in", pw / 2, 118, { align: "center" });
    const size = 280;
    pdf.addImage(img, "PNG", (pw - size) / 2, 160, size, size);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22); pdf.text(loc.name, pw / 2, 160 + size + 44, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12); pdf.text("Scan this code to clock in / out at this location", pw / 2, 160 + size + 68, { align: "center" });
    pdf.save(`rosemount-qr-${safe(loc.name)}.pdf`);
  };

  const geofenced = (loc) => loc.lat != null && loc.lng != null && loc.radius_m;

  return (
    <div className="space-y-6">
      <Card className="rounded-sm border-border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold mb-1">QR Code Manager</h3>
          <p className="text-sm text-muted-foreground">Create a QR code per location with an optional GPS geofence. No limit on how many you create.</p>
        </div>
        <Button className="rounded-sm bg-red-600 hover:bg-red-700 shrink-0" onClick={() => setDialog({ mode: "add" })} data-testid="add-location-btn"><Plus className="w-4 h-4 mr-1" /> Add Location</Button>
      </Card>

      {list.length === 0 ? (
        <div className="text-center text-muted-foreground py-10">No QR locations yet. Click "Add Location" to create your first one.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((loc) => (
            <Card key={loc.id} className="rounded-sm border-border p-4 flex flex-col items-center text-center" data-testid={`location-card-${loc.id}`}>
              <div className="font-heading font-semibold text-lg">{loc.name}</div>
              {loc.address && <div className="text-xs text-muted-foreground mb-1 line-clamp-2">{loc.address}</div>}
              <div className="mb-3">
                {geofenced(loc)
                  ? <Badge className="bg-green-600 rounded-sm" data-testid={`geofence-badge-${loc.id}`}><MapPin className="w-3 h-3 mr-1" /> Geofence {loc.radius_m}m</Badge>
                  : <Badge variant="outline" className="rounded-sm border-slate-300 text-muted-foreground" data-testid={`geofence-badge-${loc.id}`}>No geofence</Badge>}
              </div>
              <div ref={(el) => (refs.current[loc.id] = el)} className="bg-white p-3 border border-border rounded-sm">
                <QRCodeCanvas value={`${origin}/?loc=${loc.id}`} size={512} level="H" includeMargin fgColor="#0f172a" style={{ width: 140, height: 140 }} />
              </div>
              <div className="grid grid-cols-2 gap-2 w-full mt-4">
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => downloadPng(loc)} data-testid={`download-png-${loc.id}`}><Download className="w-4 h-4 mr-1" /> PNG</Button>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => downloadPdf(loc)} data-testid={`download-pdf-${loc.id}`}><Download className="w-4 h-4 mr-1" /> PDF</Button>
                <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => setDialog({ mode: "edit", loc })} data-testid={`edit-location-${loc.id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                <Button size="sm" variant="ghost" className="rounded-sm text-red-600" onClick={() => setDelTarget(loc)} data-testid={`delete-location-${loc.id}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {dialog && <LocationDialog mode={dialog.mode} loc={dialog.loc} onClose={() => setDialog(null)} onSaved={async () => { await load(); setDialog(null); }} />}

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete "{delTarget?.name}"?</DialogTitle>
            <DialogDescription>Any printed copies of this QR code will stop working. Past clock-in records keep their saved location name.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700" onClick={del} data-testid="confirm-delete-location">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocationDialog({ mode, loc, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    name: loc?.name || "",
    address: loc?.address || "",
    lat: loc?.lat ?? "",
    lng: loc?.lng ?? "",
    radius_m: loc?.radius_m ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const useCurrent = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (p) => { set("lat", p.coords.latitude.toFixed(6)); set("lng", p.coords.longitude.toFixed(6)); toast.success("Current location captured"); },
      () => toast.error("Couldn't get current location — check browser permissions"),
      { timeout: 8000 }
    );
  };
  const geocode = async () => {
    if (!form.address.trim()) return toast.error("Enter an address first");
    setGeoBusy(true);
    try {
      const { data } = await api.get("/admin/geocode", { params: { q: form.address } });
      set("lat", Number(data.lat).toFixed(6));
      set("lng", Number(data.lng).toFixed(6));
      toast.success("Address located");
    } catch (e) { toast.error(apiErr(e)); } finally { setGeoBusy(false); }
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Location name is required");
    const hasLat = form.lat !== "" && form.lat != null;
    const hasLng = form.lng !== "" && form.lng != null;
    const hasR = form.radius_m !== "" && form.radius_m != null;
    if ((hasLat || hasLng || hasR) && !(hasLat && hasLng && hasR)) {
      return toast.error("For a geofence, set latitude, longitude AND radius (or leave all three blank).");
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        address: form.address || null,
        lat: hasLat ? Number(form.lat) : null,
        lng: hasLng ? Number(form.lng) : null,
        radius_m: hasR ? Number(form.radius_m) : null,
      };
      if (isEdit) await api.put(`/admin/locations/${loc.id}`, payload);
      else await api.post(`/admin/locations`, payload);
      toast.success(isEdit ? "Location updated" : "QR location created");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md max-h-[90vh] overflow-auto" data-testid="location-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Location" : "Add Location"}</DialogTitle>
          <DialogDescription>Name the location and optionally set a GPS geofence so clock-ins are only allowed on site.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm mb-1 block">Location name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Front Workshop" className="rounded-sm border-slate-300 focus-visible:ring-red-600" data-testid="location-name-input" />
          </div>
          <div>
            <Label className="text-sm mb-1 block">Workplace address (optional)</Label>
            <div className="flex gap-2">
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Example St, Melbourne VIC" className="rounded-sm border-slate-300" data-testid="location-address-input" />
              <Button type="button" variant="outline" className="rounded-sm shrink-0" disabled={geoBusy} onClick={geocode} data-testid="geocode-btn">{geoBusy ? "…" : "Locate"}</Button>
            </div>
          </div>
          <div className="rounded-sm border border-border p-3 space-y-3 bg-muted/40">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">GPS Geofence (optional)</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={useCurrent} data-testid="use-current-location-btn"><MapPin className="w-4 h-4 mr-1" /> Use current</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Latitude</Label>
                <Input value={form.lat} onChange={(e) => set("lat", e.target.value)} placeholder="-37.8136" className="rounded-sm border-slate-300 font-mono text-sm" data-testid="location-lat-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Longitude</Label>
                <Input value={form.lng} onChange={(e) => set("lng", e.target.value)} placeholder="144.9631" className="rounded-sm border-slate-300 font-mono text-sm" data-testid="location-lng-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Radius (metres)</Label>
              <Input type="number" value={form.radius_m} onChange={(e) => set("radius_m", e.target.value)} placeholder="e.g. 100" className="rounded-sm border-slate-300 font-mono text-sm" data-testid="location-radius-input" />
            </div>
            <p className="text-xs text-muted-foreground">Leave all three blank to disable geofencing for this location.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={busy} onClick={save} data-testid="save-location-btn">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Settings ---------------- */
function PayrollCategoriesCard() {
  const [list, setList] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const load = () => api.get("/admin/payroll-categories").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!newLabel.trim()) return toast.error("Enter a category name");
    try { await api.post("/admin/payroll-categories", { label: newLabel.trim() }); toast.success("Category added"); setNewLabel(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async (id) => {
    if (!editLabel.trim()) return toast.error("Enter a category name");
    try { await api.put(`/admin/payroll-categories/${id}`, { label: editLabel.trim() }); toast.success("Category renamed"); setEditId(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/admin/payroll-categories/${delTarget.id}`); toast.success("Category removed"); setDelTarget(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="rounded-sm border-border p-5 space-y-3" data-testid="payroll-categories-card">
      <h3 className="font-heading font-semibold">Payroll Categories</h3>
      <p className="text-sm text-muted-foreground">These appear in the timesheet category dropdown and payroll exports. Rename or remove any category, or add your own.</p>
      <div className="divide-y divide-border border border-border rounded-sm">
        {list.length === 0 && <p className="text-xs text-muted-foreground p-3" data-testid="no-cats">No categories.</p>}
        {list.map((c) => (
          <div key={c.id} className="flex items-center gap-2 p-2.5" data-testid={`cat-row-${c.id}`}>
            {editId === c.id ? (
              <>
                <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="rounded-sm border-slate-300 h-8 flex-1" data-testid={`edit-cat-input-${c.id}`} />
                <Button size="sm" className="rounded-sm h-8 bg-green-600 hover:bg-green-700" onClick={() => saveEdit(c.id)} data-testid={`save-cat-${c.id}`}><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setEditId(null)} data-testid={`cancel-cat-${c.id}`}><X className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm font-medium" data-testid={`cat-label-${c.id}`}>
                  {c.label}
                  {!c.custom && <Badge variant="outline" className="rounded-sm ml-2 text-[10px] border-slate-300">Default</Badge>}
                  {c.in_use > 0 && <span className="ml-2 text-[10px] text-muted-foreground">· used on {c.in_use} entr{c.in_use === 1 ? "y" : "ies"}</span>}
                </span>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => { setEditId(c.id); setEditLabel(c.label); }} data-testid={`edit-cat-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => setDelTarget(c)} data-testid={`delete-cat-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New category name (e.g. Sick Leave)" className="rounded-sm border-slate-300 h-9 flex-1" data-testid="new-cat-input" />
        <Button className="rounded-sm bg-red-600 hover:bg-red-700 h-9" onClick={add} data-testid="add-cat-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>

      {delTarget && (
        <Dialog open onOpenChange={() => setDelTarget(null)}>
          <DialogContent className="rounded-sm max-w-sm" data-testid="delete-cat-dialog">
            <DialogHeader>
              <DialogTitle>Remove "{delTarget.label}"?</DialogTitle>
              <DialogDescription>
                {delTarget.in_use > 0
                  ? `⚠️ This category is currently used on ${delTarget.in_use} timesheet ${delTarget.in_use === 1 ? "entry" : "entries"}. Removing it won't change those existing entries (they keep the "${delTarget.label}" label), but it will no longer be available to select. Continue?`
                  : "This category isn't used on any timesheet entry. It will be removed from the dropdown. Continue?"}
                {delTarget.core && " Note: this is a core payroll category used by auto-split for clocked hours."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setDelTarget(null)} data-testid="delete-cat-cancel">Cancel</Button>
              <Button className="rounded-sm bg-red-600 hover:bg-red-700" onClick={confirmDelete} data-testid="delete-cat-confirm">Remove Category</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

function SettingsTab() {
  const [alertEmail, setAlertEmail] = useState("");
  const [curPin, setCurPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [dangerPin, setDangerPin] = useState("");
  const [pinSet, setPinSet] = useState(true);
  const [tsCurPin, setTsCurPin] = useState("");
  const [tsNewPin, setTsNewPin] = useState("");
  const [tsPinSet, setTsPinSet] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetPin, setResetPin] = useState("");

  useEffect(() => { api.get("/admin/settings").then((r) => { setAlertEmail(r.data.alert_email || ""); setPinSet(!!r.data.security_pin_configured); setTsPinSet(!!r.data.timesheet_lock_pin_configured); }); }, []);

  const saveEmail = async () => {
    try { await api.put("/admin/settings", { alert_email: alertEmail }); toast.success("Alert email saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const [sending, setSending] = useState(false);
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("auto");
  useEffect(() => { api.get("/admin/weekly-summary/weeks?count=12").then((r) => setWeeks(r.data)).catch(() => {}); }, []);
  const sendWeeklyNow = async () => {
    setSending(true);
    try {
      const url = selectedWeek === "auto"
        ? "/admin/weekly-summary/send-now"
        : `/admin/weekly-summary/send-now?week_start=${selectedWeek}`;
      const { data } = await api.post(url);
      toast.success("Weekly summary sent", { description: `Week ${data.week} · emailed to ${data.recipient}` });
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };
  const savePin = async () => {
    if (!/^\d{4}$/.test(newPin)) return toast.error("Enter a 4-digit PIN");
    try {
      await api.put("/admin/settings", { current_security_pin: curPin, new_security_pin: newPin });
      toast.success(pinSet ? "Security PIN updated" : "Security PIN set");
      setCurPin(""); setNewPin(""); setPinSet(true);
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveTimesheetPin = async () => {
    if (!/^\d{4}$/.test(tsNewPin)) return toast.error("Enter a 4-digit PIN");
    try {
      await api.put("/admin/settings", { current_timesheet_pin: tsCurPin, new_timesheet_pin: tsNewPin });
      toast.success(tsPinSet ? "Timesheet Lock PIN updated" : "Timesheet Lock PIN set");
      setTsCurPin(""); setTsNewPin(""); setTsPinSet(true);
    }
    catch (e) { toast.error(apiErr(e)); }
  };
  const savePw = async () => {
    try { await api.post("/auth/change-password", { current_password: curPw, new_password: newPw }); toast.success("Password changed"); setCurPw(""); setNewPw(""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const clearAudit = async () => {
    try { await api.post("/admin/clear-audit-log", { security_pin: dangerPin }); toast.success("Audit log cleared"); setDangerPin(""); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const downloadAudit = async () => {
    try {
      const { data } = await api.get("/admin/audit-log");
      if (!data.length) return toast.info("Audit log is empty");
      const rows = [["Time", "Action", "Detail", "Admin"]];
      data.forEach((l) => rows.push([fmtDateTime(l.timestamp), l.action || "", l.detail || "", l.admin || ""]));
      const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const resetPinWithPw = async () => {
    if (!resetPw) return toast.error("Enter your admin password");
    if (!/^\d{4}$/.test(resetPin)) return toast.error("Enter a 4-digit PIN");
    try {
      await api.post("/admin/reset-security-pin", { admin_password: resetPw, new_pin: resetPin });
      toast.success("Security PIN reset");
      setResetPw(""); setResetPin(""); setShowReset(false); setPinSet(true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
      <Card className="rounded-sm border-border p-5 space-y-3">
        <h3 className="font-heading font-semibold">Alert Email Recipient</h3>
        <p className="text-sm text-muted-foreground">Long-shift alerts and weekly summaries are sent here.</p>
        <Input value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)} placeholder="manager@company.com" className="rounded-sm border-slate-300" data-testid="alert-email-input" />
        <Button className="rounded-sm bg-slate-700 hover:bg-slate-800" onClick={saveEmail} data-testid="save-alert-email">Save Email</Button>
        <div className="pt-3 mt-1 border-t border-border">
          <div className="text-sm font-semibold">Weekly Summary Email</div>
          <p className="text-sm text-muted-foreground mb-2">Sends automatically every Wednesday 6pm (Melbourne). Pick a week and send it now without waiting.</p>
          <Select value={selectedWeek} onValueChange={setSelectedWeek}>
            <SelectTrigger className="rounded-sm border-slate-300 h-9 mb-2" data-testid="summary-week-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto" data-testid="summary-week-auto">Auto (latest week with data)</SelectItem>
              {weeks.map((w) => (
                <SelectItem key={w.week_start} value={w.week_start} data-testid={`summary-week-${w.week_start}`}>
                  {w.label}{w.current ? " (current)" : ""}{w.has_data ? "" : " — no data"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-sm border-red-600 text-red-600 hover:bg-red-50" disabled={sending} onClick={sendWeeklyNow} data-testid="send-weekly-now-btn">
            <Mail className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send Now"}
          </Button>
        </div>
      </Card>

      <Card className="rounded-sm border-border p-5 space-y-3">
        <h3 className="font-heading font-semibold">Change Admin Password</h3>
        <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="Current password" className="rounded-sm border-slate-300" data-testid="cur-pw-input" />
        <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" className="rounded-sm border-slate-300" data-testid="new-pw-input" />
        <Button className="rounded-sm bg-slate-700 hover:bg-slate-800" onClick={savePw} data-testid="save-pw-btn">Update Password</Button>
      </Card>

      <PayrollCategoriesCard />

      <Card className="rounded-sm border-border p-5 space-y-3">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Lock className="w-4 h-4" /> Timesheet Lock PIN</h3>
        <p className="text-sm text-muted-foreground">
          {tsPinSet
            ? "Used exclusively to lock/unlock individual employee timesheets. Separate from the admin password and the Danger Zone security PIN. Enter the current PIN, then a new one."
            : "No Timesheet Lock PIN set yet. This PIN is used only to lock/unlock employee timesheets — separate from the admin password and Danger Zone security PIN. Set a 4-digit PIN to enable locking (no current PIN needed for first setup)."}
        </p>
        {tsPinSet && (
          <Input value={tsCurPin} maxLength={4} inputMode="numeric" onChange={(e) => setTsCurPin(e.target.value.replace(/\D/g, ""))} placeholder="Current Timesheet Lock PIN" className="rounded-sm border-slate-300 font-mono" data-testid="ts-cur-pin-input" />
        )}
        <Input value={tsNewPin} maxLength={4} inputMode="numeric" onChange={(e) => setTsNewPin(e.target.value.replace(/\D/g, ""))} placeholder={tsPinSet ? "New 4-digit PIN" : "4-digit PIN"} className="rounded-sm border-slate-300 font-mono" data-testid="ts-new-pin-input" />
        <Button className="rounded-sm bg-slate-700 hover:bg-slate-800" onClick={saveTimesheetPin} data-testid="save-ts-pin-btn">{tsPinSet ? "Update Timesheet Lock PIN" : "Set Timesheet Lock PIN"}</Button>
      </Card>

      <Card className="rounded-sm border-2 border-red-600 p-5 space-y-4">
        <h3 className="font-heading font-semibold text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Danger Zone</h3>

        <div className="space-y-2 pb-4 border-b border-border">
          <div className="text-sm font-semibold">{pinSet ? "Change Security PIN" : "Set Security PIN"}</div>
          <p className="text-sm text-muted-foreground">{pinSet ? "Enter your current PIN, then choose a new one. Used to confirm sensitive actions." : "No security PIN set yet. Choose a 4-digit PIN to enable sensitive actions — no current PIN needed for the first setup."}</p>
          {pinSet && (
            <Input value={curPin} maxLength={4} inputMode="numeric" onChange={(e) => setCurPin(e.target.value.replace(/\D/g, ""))} placeholder="Current PIN" className="rounded-sm border-slate-300 font-mono" data-testid="cur-pin-input" />
          )}
          <Input value={newPin} maxLength={4} inputMode="numeric" onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} placeholder={pinSet ? "New 4-digit PIN" : "4-digit PIN"} className="rounded-sm border-slate-300 font-mono" data-testid="new-pin-input" />
          <Button className="rounded-sm bg-slate-700 hover:bg-slate-800" onClick={savePin} data-testid="save-pin-btn">{pinSet ? "Update PIN" : "Set PIN"}</Button>
          {pinSet && !showReset && (
            <button type="button" className="text-xs text-red-600 hover:underline block pt-1" onClick={() => setShowReset(true)} data-testid="forgot-pin-btn">Forgot PIN? Reset with admin password</button>
          )}
          {showReset && (
            <div className="rounded-sm border border-slate-300 bg-slate-50 p-3 space-y-2 mt-1" data-testid="reset-pin-panel">
              <div className="text-xs font-semibold">Reset a forgotten PIN — confirm with your admin password.</div>
              <Input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Admin password" className="rounded-sm border-slate-300" data-testid="reset-pin-password" />
              <Input value={resetPin} maxLength={4} inputMode="numeric" onChange={(e) => setResetPin(e.target.value.replace(/\D/g, ""))} placeholder="New 4-digit PIN" className="rounded-sm border-slate-300 font-mono" data-testid="reset-pin-new" />
              <div className="flex gap-2">
                <Button size="sm" className="rounded-sm bg-red-600 hover:bg-red-700" onClick={resetPinWithPw} data-testid="reset-pin-submit">Reset PIN</Button>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setShowReset(false); setResetPw(""); setResetPin(""); }} data-testid="reset-pin-cancel">Cancel</Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold">Clear Audit Log</div>
          <p className="text-sm text-muted-foreground">Permanently deletes all audit entries. This cannot be undone. Download a backup first, then confirm with your security PIN.</p>
          <Button variant="outline" className="rounded-sm border-slate-400 w-full" onClick={downloadAudit} data-testid="download-audit-btn"><Download className="w-4 h-4 mr-2" /> Download Audit Log (CSV)</Button>
          <Input value={dangerPin} maxLength={4} inputMode="numeric" onChange={(e) => setDangerPin(e.target.value.replace(/\D/g, ""))} placeholder="Security PIN" className="rounded-sm border-slate-300 font-mono" data-testid="danger-pin-input" />
          <Button className="rounded-sm bg-red-600 hover:bg-red-700" onClick={clearAudit} data-testid="clear-audit-btn"><Trash2 className="w-4 h-4 mr-2" /> Clear Audit Log</Button>
        </div>
      </Card>
    </div>
  );
}
